import { joiResolver } from '@hookform/resolvers/joi'
import { CircularProgress } from '@mui/material'
import { styled } from '@mui/material/styles'
import Joi from 'joi'
import { createContext, ReactNode, useContext } from 'react'
import { DeepPartial, FieldValues, FormProvider, SubmitErrorHandler, SubmitHandler, UnpackNestedValue, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type DefaultValues<T> = UnpackNestedValue<DeepPartial<T>>

type FormDefaultsProviderProps<T> = {
  children: ReactNode
  defaultValues?: DefaultValues<T>
}

type FormProps<T extends FieldValues> = {
  children: ReactNode
  defaultValues?: DefaultValues<T>
  disabled?: boolean
  onError?: SubmitErrorHandler<T>
  onSubmit: SubmitHandler<T>
  validationSchema: Joi.ObjectSchema<T>
}

const FieldSet = styled('fieldset')`
  border: none;
  margin: 0;
  padding: 0;
  position: relative;
  transition: opacity 200ms ease;
  max-width: calc(100% - 4px);
`

const OverlayBackground = styled('div')`
  background-color: #fff;
  bottom: -2px;
  display: grid;
  left: -2px;
  place-items: center;
  position: absolute;
  right: -2px;
  top: -2px;
  opacity: 0.75;
  z-index: 1;
`

const Message = styled('div')`
  align-items: center;
  background-color: #fff;
  border-radius: 0.25rem;
  border: 1px solid gray;
  box-shadow: 0 0 2px rgb(0 0 0 / 50%);
  display: flex;
  gap: 0.25rem;
  left: 50%;
  padding: 1rem;
  position: absolute;
  top: 40%;
  transform: translate(-50%, -50%);
  z-index: 2;
`

const Overlay = () => {
  const { t } = useTranslation()
  return (
    <>
      <OverlayBackground />
      <Message>
        <CircularProgress size="0.95rem" />
        {t('common.label.availabilityWaitingMessage')}
      </Message>
    </>
  )
}

const formDefaultsContext = createContext(undefined)

const FormDefaultsProvider = <T extends unknown>({ children, defaultValues }: FormDefaultsProviderProps<T>) => (
  // @ts-expect-error Can't figure out the typing here
  <formDefaultsContext.Provider value={defaultValues}>{children}</formDefaultsContext.Provider>
)

const useFormDefaults = <T extends unknown>() => useContext(formDefaultsContext) as T

const Form = <T extends Record<string, any> = Record<string, any>>({
  children,
  defaultValues,
  disabled,
  onError,
  onSubmit,
  validationSchema,
}: FormProps<T>) => {
  const resolver = joiResolver(validationSchema)
  const formProps = useForm<T>({ defaultValues, mode: 'onTouched', resolver })

  return (
    <FormProvider {...formProps}>
      <FormDefaultsProvider<T> defaultValues={defaultValues}>
        <form autoComplete="off" onSubmit={formProps.handleSubmit(onSubmit, onError)}>
          <FieldSet disabled={disabled}>
            {disabled && <Overlay />}
            {children}
          </FieldSet>
        </form>
      </FormDefaultsProvider>
    </FormProvider>
  )
}

export { Form, useFormDefaults }
